<template>
    <div class="mail-selector" :class="{ 'has-warning': showWarning }">
        <div ref="editorContainer" class="editor-container" @click="focusEditor">
            <editor-content :editor="editor" class="mail-editor-content" @paste.prevent.native="handlePaste" />
        </div>

        <div v-if="showDropdown" class="dropdown-container">
            <div v-if="loading" class="loading-indicator">
                <i class="material-icons">refresh</i>
                <span>Yükleniyor...</span>
            </div>
            <template v-else>
                <div v-if="filteredOptions.length === 0" class="no-results">
                    Sonuç bulunamadı
                </div>
                <template v-else>
                    <div v-for="(group, index) in groupedOptions" :key="index" class="option-group">
                        <div v-if="group.label" class="group-label">{{ group.label }}</div>
                        <div v-for="option in group.options" :key="option.value" class="option-item"
                            :class="{ 'selected': selectedIndex === getGlobalIndex(index, option) }"
                            @click="selectOption(option)">
                            <div class="option-content">
                                <span class="option-label">{{ option.label }}</span>
                                <span class="option-mail">{{ option.value }}</span>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
        </div>

        <div v-if="showWarning && selectedEmails.length > 1" class="warning-message">
            <i class="material-icons">warning</i>
            <span>Girilen e-posta adreslerine tek tek gönderim yapılacaktır. Tek mail göndermek için CC ekleyiniz.</span>
        </div>
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import { Extension } from '@tiptap/core'
import Placeholder from '@tiptap/extension-placeholder'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import { MailTag } from './extensions/MailTagExtension'

export default {
    name: 'MailSelector',
    components: {
        EditorContent,
    },
    model: {
        prop: 'value',
        event: 'input'
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        options: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: 'E-posta adresi ekleyin...',
        },
        multiple: {
            type: Boolean,
            default: true,
        },
        showWarningForMultiple: {
            type: Boolean,
            default: false,
        },
        autoFocus: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editor: null,
            searchText: '',
            showDropdown: false,
            selectedIndex: -1,
            loading: false,
            selectedEmails: [],
            skipNextUpdate: false
        }
    },
    computed: {
        filteredOptions() {
            const searchText = this.searchText.trim();
            if (!searchText) return this.options;
            return this.options.filter(option => {
                const label = option.label.trim().toLowerCase()
                const value = option.value.trim().toLowerCase()
                const searchLower = searchText.toLowerCase()
                return label.includes(searchLower) || value.includes(searchLower)
            })
        },
        groupedOptions() {
            // Seçenekleri gruplandırıyoruz
            const groups = {}

            this.filteredOptions.forEach(option => {
                const type = option.type || 'user'
                if (!groups[type]) {
                    groups[type] = {
                        label: this.getGroupLabel(type),
                        options: []
                    }
                }
                groups[type].options.push(option)
            })

            // Grupları sıralayarak dizi haline getiriyoruz
            return Object.values(groups).sort((a, b) => {
                // Sıralama önceliğini belirleyebilirsiniz
                const order = { user: 1, visitor: 2, department: 3 }
                return (order[a.options[0].type] || 99) - (order[b.options[0].type] || 99)
            })
        },
        showWarning() {
            return this.showWarningForMultiple && this.multiple && this.selectedEmails.length > 1
        }
    },
    watch: {
        value: {
            handler(newVal) {
                if (this.editor && JSON.stringify(newVal) !== JSON.stringify(this.selectedEmails)) {
                    this.updateEditorContent(newVal)
                }
            },
            deep: true
        },
        autoFocus(newValue) {
            if (newValue && this.editor) {
                this.editor.commands.focus();
            }
        },
    },
    mounted() {
        this.initEditor()
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        // Editörü ve olay dinleyicilerini temizliyoruz
        if (this.editor) {
            this.editor.destroy()
        }
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        initEditor() {
            this.editor = new Editor({
                extensions: [
                    Document,
                    Paragraph,
                    Text,
                    MailTag,
                    Placeholder.configure({
                        placeholder: this.placeholder,
                    }),
                ],
                content: '',
                editorProps: {
                    handleKeyDown: this.handleEditorKeyDown,
                },
                onUpdate: this.onEditorUpdate,
                onFocus: this.onEditorFocus,
                autofocus: this.autoFocus
            })

            // Başlangıç değerlerini ayarlıyoruz
            if (this.value && this.value.length > 0) {
                this.updateEditorContent(this.value)
            }
        },

        handleEditorKeyDown(view, event) {
            // Ctrl+@ veya Ctrl+Space kombinasyonunu engelle
            if ((event.key === '@' || event.key === ' ') && (event.ctrlKey || event.metaKey)) {
                event.preventDefault()
                return true
            }

            // Backspace tuşu için tag silme
            if (event.key === 'Backspace') {
                const { empty, from } = view.state.selection

                if (empty) {
                    const prev = from - 1
                    const node = view.state.doc.nodeAt(prev)

                    if (node && node.type.name === 'mailTag') {
                        this.editor.commands.deleteRange({ from: prev, to: from })
                        return true
                    }
                }
            }

            // Enter tuşu için metin varsa mail ekle, dropdown açıksa seçenek seç
            if (event.key === 'Enter') {
                if (this.showDropdown && this.filteredOptions.length > 0) {
                    event.preventDefault()
                    const selected = this.getSelectedOption()
                    if (selected) {
                        this.selectOption(selected)
                    }
                    return true
                } else {
                    // Dropdown yoksa ve metin varsa e-posta olarak ekle
                    const text = this.editor.getText()
                    if (text && text.trim()) {
                        event.preventDefault()
                        this.addEmailFromText(text)
                        return true
                    }
                }

                // Enter tuşuna basıldığında alt satıra geçişi engelle
                event.preventDefault()
                return true
            }

            // Tab tuşu için dropdown'dan seçim yapma
            if (event.key === 'Tab' && this.showDropdown && this.filteredOptions.length > 0) {
                event.preventDefault()
                const selected = this.getSelectedOption()
                if (selected) {
                    this.selectOption(selected)
                }
                return true
            }

            // Arrow tuşları için dropdown içinde gezinme
            if (event.key === 'ArrowDown' && this.showDropdown && this.filteredOptions.length > 0) {
                event.preventDefault()
                this.navigateDropdown(1)
                return true
            }

            if (event.key === 'ArrowUp' && this.showDropdown && this.filteredOptions.length > 0) {
                event.preventDefault()
                this.navigateDropdown(-1)
                return true
            }

            // Normal @ veya space veya virgül için mail ekleme işlemi
            if ((event.key === ' ' || event.key === ',') && !event.ctrlKey && !event.metaKey) {
                const text = this.editor.getText()
                if (text && text.trim()) {
                    event.preventDefault()
                    this.addEmailFromText(text)
                    return true
                }
            }

            return false
        },

        onEditorUpdate({ editor }) {
            if (this.skipNextUpdate) {
                this.skipNextUpdate = false
                return
            }

            const content = editor.getText()
            this.searchText = content.trim()

            if (this.searchText) {
                this.showDropdown = true
                this.$emit('search', this.searchText)
            } else {
                this.showDropdown = false
            }

            // Seçili e-postaları güncelleyelim
            this.updateSelectedEmails()
        },

        onEditorFocus() {
            // Odaklandığında dropdown'ı gösterebiliriz
            this.showDropdownIfNeeded()
        },

        showDropdownIfNeeded() {
            const content = this.editor.getText()
            if (content && content.trim()) {
                this.showDropdown = true
                this.$emit('search', content.trim())
            }
        },

        // Seçenekler arasında gezinme fonksiyonu
        navigateDropdown(direction) {
            const totalOptions = this.getTotalOptions()
            if (totalOptions === 0) return

            this.selectedIndex = (this.selectedIndex + direction + totalOptions) % totalOptions

            // Seçili öğeye scroll yap
            this.$nextTick(() => {
                const selectedItem = document.querySelector('.option-item.selected')
                if (selectedItem) {
                    const container = document.querySelector('.dropdown-container')
                    if (container) {
                        // Görünür bölgede değilse scroll et
                        const itemTop = selectedItem.offsetTop
                        const itemBottom = itemTop + selectedItem.offsetHeight
                        const containerTop = container.scrollTop
                        const containerBottom = containerTop + container.offsetHeight

                        if (itemTop < containerTop) {
                            // Eğer seçili öğe yukarıda görünmüyorsa
                            container.scrollTop = itemTop
                        } else if (itemBottom > containerBottom) {
                            // Eğer seçili öğe aşağıda görünmüyorsa
                            container.scrollTop = itemBottom - container.offsetHeight
                        }
                    }
                }
            })
        },

        // Toplam seçenek sayısını hesaplama
        getTotalOptions() {
            let total = 0
            this.groupedOptions.forEach(group => {
                total += group.options.length
            })
            return total
        },

        // Seçili seçeneği alma
        getSelectedOption() {
            if (this.selectedIndex < 0) return null

            let currentIndex = 0
            for (let groupIndex = 0; groupIndex < this.groupedOptions.length; groupIndex++) {
                const group = this.groupedOptions[groupIndex]
                for (let optionIndex = 0; optionIndex < group.options.length; optionIndex++) {
                    if (currentIndex === this.selectedIndex) {
                        return group.options[optionIndex]
                    }
                    currentIndex++
                }
            }

            return null
        },

        // Global indeksi alma (grup içindeki indeks)
        getGlobalIndex(groupIndex, option) {
            let index = 0
            for (let i = 0; i < groupIndex; i++) {
                index += this.groupedOptions[i].options.length
            }
            index += this.groupedOptions[groupIndex].options.indexOf(option)
            return index
        },

        // Grup etiketini alma
        getGroupLabel(type) {
            const labels = {
                user: 'Kullanıcılar',
                visitor: 'Ziyaretçiler',
                department: 'Departmanlar',
            }

            return labels[type] || 'Diğer'
        },

        // Seçenek seçildiğinde
        selectOption(option) {
            this.skipNextUpdate = true

            // Mevcut mail tag'lerini saklayalım
            const existingEmails = [...this.selectedEmails]

            // İçeriği temizle
            this.editor.commands.setContent('')

            // Tek seçim modunda ve mail varsa, önceki mailleri temizle
            if (!this.multiple && existingEmails.length > 0) {
                existingEmails.length = 0
            }

            // Önce varolan mailleri ekle
            existingEmails.forEach((existingEmail, index) => {
                const existing = this.options.find(opt => opt.value === existingEmail) || { value: existingEmail, type: 'user' }
                this.skipNextUpdate = true
                this.editor.commands.insertContent({
                    type: 'mailTag',
                    attrs: {
                        mail: existing.value,
                        type: existing.type || 'user',
                        valid: this.validateEmail(existing.value)
                    }
                })

                // Her mail arasına boşluk ekle
                this.skipNextUpdate = true
                this.editor.commands.insertContent(' ')
            })

            // Yeni seçilen maili ekle (aynısı yoksa)
            if (!existingEmails.includes(option.value)) {
                this.skipNextUpdate = true
                this.editor.commands.insertContent({
                    type: 'mailTag',
                    attrs: {
                        mail: option.value,
                        type: option.type || 'user',
                        valid: this.validateEmail(option.value)
                    }
                })

                // İmlecin görünmesi için ekstra boşluk ekle
                this.skipNextUpdate = true
                this.editor.commands.insertContent(' ')
            }

            this.searchText = ''
            this.showDropdown = false
            this.selectedIndex = -1

            // Mailleri güncelle
            setTimeout(() => {
                this.updateSelectedEmails()
            }, 0)
        },

        // E-posta ekleme
        addEmail(email, type = 'user') {
            // E-posta geçerliliğini kontrol et
            const isValid = this.validateEmail(email)

            // Mevcut mail tag'lerini saklayalım
            const existingEmails = [...this.selectedEmails]

            // Eğer çoklu seçim devre dışı ise, önceki mailleri temizle
            if (!this.multiple && existingEmails.length > 0) {
                this.skipNextUpdate = true
                this.editor.commands.setContent('')
                existingEmails.length = 0 // Array'i boşalt
            }

            // Yeni mail ekleyelim
            this.skipNextUpdate = true
            this.editor.commands.insertContent({
                type: 'mailTag',
                attrs: {
                    mail: email,
                    type: type,
                    valid: isValid
                }
            })

            // İmlecin daha doğal görünmesi için boşluk ekleyelim
            this.skipNextUpdate = true
            this.editor.commands.insertContent(' ')

            // Seçili mailleri güncelle
            setTimeout(() => {
                this.updateSelectedEmails()
            }, 0)
        },

        // Metinden e-posta ekleme
        addEmailFromText(text) {
            if (!text || !text.trim()) return

            // Metni işle ve e-posta olarak ekle
            const email = text.trim()

            // Mevcut mail tag'lerini saklayalım
            const existingEmails = [...this.selectedEmails]

            // Boşluklar, virgüller veya noktalı virgüllerle ayrılmış birden fazla e-posta olabilir
            const emails = email.split(/[\s,;]+/).filter(Boolean)

            if (emails.length > 0) {
                // İçeriği temizle ama önceki tag'leri kaybetme
                this.skipNextUpdate = true
                this.editor.commands.setContent('')

                // Eğer tek mail ekleme (multiple=false) durumunda ve önceden mail varsa onları temizle
                if (!this.multiple && existingEmails.length > 0) {
                    // Önceki mailleri temizle
                    existingEmails.length = 0
                }

                // Önce var olan mailleri ekleyelim
                existingEmails.forEach(existingEmail => {
                    const existing = this.options.find(opt => opt.value === existingEmail) || { value: existingEmail, type: 'user' }
                    this.skipNextUpdate = true
                    this.editor.commands.insertContent({
                        type: 'mailTag',
                        attrs: {
                            mail: existing.value,
                            type: existing.type || 'user',
                            valid: this.validateEmail(existing.value)
                        }
                    })
                })

                // Sonra yeni mailleri ekleyelim (eğer çoklu ekleme izni varsa veya hiç mail yoksa)
                if (this.multiple || existingEmails.length === 0) {
                    // E-postaları ekle
                    emails.forEach(mail => {
                        // Sadece aynı mail tekrar eklenmesini önle
                        if (!existingEmails.includes(mail)) {
                            this.skipNextUpdate = true
                            this.editor.commands.insertContent({
                                type: 'mailTag',
                                attrs: {
                                    mail: mail,
                                    type: 'user',
                                    valid: this.validateEmail(mail)
                                }
                            })
                        }
                    })
                }
            }

            // Seçili mailleri güncelle
            setTimeout(() => {
                this.updateSelectedEmails()
            }, 0)

            this.skipNextUpdate = true;
            this.editor.commands.insertContent(' ')
        },

        // Editöre focus yap
        focusEditor() {
            if (this.editor) {
                this.editor.commands.focus('end')
            }
        },

        // Yapıştırma olayını ele alma
        handlePaste(event) {
            // Native yapıştırma olayını engellediğimizden clipboardData'yı manuel olarak alalım
            const clipboardData = event.clipboardData || window.clipboardData
            if (!clipboardData) return

            const pastedData = clipboardData.getData('text')

            if (pastedData) {
                // Mevcut mail tag'lerini saklayalım
                const existingEmails = [...this.selectedEmails]

                // Boşluklar, virgüller veya noktalı virgüllerle ayrılmış e-postaları işle
                const emails = pastedData.split(/[\s,;]+/).filter(Boolean)

                if (emails.length > 0) {
                    // İçeriği temizle
                    this.skipNextUpdate = true
                    this.editor.commands.setContent('')

                    // Eğer çoklu seçim yoksa ve zaten mail varsa
                    if (!this.multiple && existingEmails.length > 0) {
                        // Sadece ilk maili ekleyelim
                        this.skipNextUpdate = true
                        this.editor.commands.insertContent({
                            type: 'mailTag',
                            attrs: {
                                mail: emails[0],
                                type: 'user',
                                valid: this.validateEmail(emails[0])
                            }
                        })

                        // İmlecin görünmesi için ekstra boşluk ekle
                        this.skipNextUpdate = true
                        this.editor.commands.insertContent(' ')
                    } else {
                        // Önce varolan mailleri ekle
                        existingEmails.forEach(existingEmail => {
                            const existing = this.options.find(opt => opt.value === existingEmail) || { value: existingEmail, type: 'user' }
                            this.skipNextUpdate = true
                            this.editor.commands.insertContent({
                                type: 'mailTag',
                                attrs: {
                                    mail: existing.value,
                                    type: existing.type || 'user',
                                    valid: this.validateEmail(existing.value)
                                }
                            })

                            // Her mail arasına boşluk ekle
                            this.skipNextUpdate = true
                            this.editor.commands.insertContent(' ')
                        })

                        // Sonra yeni mailleri ekle
                        emails.forEach((email, index) => {
                            // Aynı maili tekrar eklemeyi önle
                            if (!existingEmails.includes(email)) {
                                this.skipNextUpdate = true
                                this.editor.commands.insertContent({
                                    type: 'mailTag',
                                    attrs: {
                                        mail: email,
                                        type: 'user',
                                        valid: this.validateEmail(email)
                                    }
                                })

                                // İmlecin görünmesi için ekstra boşluk ekle
                                // Son email değilse veya tek email varsa boşluk ekle
                                if (index < emails.length - 1 || emails.length === 1) {
                                    this.skipNextUpdate = true
                                    this.editor.commands.insertContent(' ')
                                }
                            }
                        })

                        // En sona bir boşluk ekleyelim
                        this.skipNextUpdate = true
                        this.editor.commands.insertContent(' ')
                    }

                    // Mailleri güncelle
                    setTimeout(() => {
                        this.updateSelectedEmails()
                    }, 0)
                }
            }
        },

        // Editör içeriğini güncelleme
        updateEditorContent(emails) {
            if (!this.editor) return

            this.skipNextUpdate = true
            this.editor.commands.clearContent()

            // Mailleri ekle
            for (let i = 0; i < emails.length; i++) {
                const email = emails[i]
                const existingOption = this.options.find(opt => opt.value === email)
                const type = existingOption ? existingOption.type : 'user'

                const isValid = this.validateEmail(email)
                this.skipNextUpdate = true
                this.editor.commands.insertContent({
                    type: 'mailTag',
                    attrs: {
                        mail: email,
                        type: type,
                        valid: isValid
                    }
                })

                // Son değilse boşluk ekle
                if (i < emails.length - 1) {
                    this.skipNextUpdate = true
                    this.editor.commands.insertContent(' ')
                }
            }

            // En sona bir boşluk ekleyelim
            this.skipNextUpdate = true
            this.editor.commands.insertContent(' ')

            setTimeout(() => {
                this.updateSelectedEmails()
            }, 0)
        },

        // Dış tıklamaları ele alma
        handleClickOutside(event) {
            if (this.$refs.editorContainer && !this.$refs.editorContainer.contains(event.target)) {
                this.showDropdown = false
            }
        },

        // E-posta doğrulama
        validateEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailRegex.test(email)
        },

        // Seçili e-postaları güncelleme
        updateSelectedEmails() {
            if (!this.editor) return

            let emails = []

            // Editör içeriğinden tüm mailTag nodlarını bul
            this.editor.state.doc.descendants((node, pos) => {
                if (node.type.name === 'mailTag') {
                    emails.push(node.attrs.mail)
                }
            });

            if (JSON.stringify(this.selectedEmails) !== JSON.stringify(emails)) {
                this.selectedEmails = emails
                this.$emit('input', emails)
            }
        }
    },
}
</script>

<style lang="scss">
.mail-selector {
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    background-color: #fff;

    &:focus-within {
        border-color: #22272d;
        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.2);
    }

    .editor-container {
        min-height: 38px;
        padding: 4px 8px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        cursor: text;

        p {
            margin: 0 !important;
            padding: 0 !important;
        }
    }

    >>>.mail-editor-content {
        width: 100%;
        outline: none;

        .ProseMirror {
            outline: none;
            min-height: 30px;
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            align-items: center;

            p {
                margin: 0 !important;
                display: inline-flex;
                flex-wrap: wrap;
                align-items: center;
                width: auto;
                min-width: 20px;
                padding: 0 !important;
                line-height: normal;
                min-height: 30px;
            }

            .ProseMirror-gapcursor {
                display: none;
            }

            &.ProseMirror-focused {
                caret-color: #22272d;
            }

            .ProseMirror-trailingBreak {
                display: none !important;
            }
        }
    }

    .dropdown-container {
        position: absolute;
        width: 100%;
        max-height: 300px;
        overflow-y: auto;
        background-color: #fff;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        z-index: 1000;
        top: 100%;
        margin-top: 4px;

        .loading-indicator {
            padding: 10px 16px;
            display: flex;
            align-items: center;

            i {
                margin-right: 8px;
                animation: spin 1s linear infinite;
            }

            @keyframes spin {
                0% {
                    transform: rotate(0deg);
                }

                100% {
                    transform: rotate(360deg);
                }
            }
        }

        .no-results {
            padding: 10px 16px;
            color: #999;
            text-align: center;
        }

        .option-group {
            .group-label {
                padding: 6px 16px;
                background-color: #f5f5f5;
                color: #666;
                font-weight: 500;
                font-size: 12px;
            }

            .option-item {
                padding: 8px 16px;
                cursor: pointer;

                &:hover,
                &.selected {
                    background-color: #f0f0f0;
                }

                .option-content {
                    display: flex;
                    flex-direction: column;

                    .option-label {
                        font-weight: 500;
                        color: #22272d;
                    }

                    .option-mail {
                        font-size: 12px;
                        color: #666;
                    }
                }
            }
        }
    }

    .warning-message {
        padding: 8px;
        background-color: rgba(255, 192, 0, 0.1);
        border-top: 1px solid #ffc000;
        color: #856404;
        display: flex;
        align-items: center;
        margin-top: 4px;
        border-radius: 4px;

        i {
            margin-right: 6px;
            font-size: 16px;
            color: #ffc000;
        }

        span {
            font-size: 12px;
        }
    }
}
</style>
